<template>
  <c-modal
    class-name="m-write-off-inventory-item"
    :active="active"
    :disable-scroll="disableScroll"
    hide-header
    @close="close"
  >
    <div class="write-off-item">
      <div class="modal-frame__header">
        <h2 class="modal-frame__title">
          Списать ТМЦ <span class="font-weight-normal">№{{ item && item.serialNumber }}</span>
          <div v-if="item && item.name" class="item-name">{{ item.name }}</div>
        </h2>
      </div>
      <div class="write-off-item-form">
        <div class="d-flex flex-wrap">
          <div class="form-group write-off-item-form__date" :class="{ 'form-group--invalid': dateError }">
            <c-input
              ref="calendar"
              v-model="form.date"
              label="Дата списания"
              type="date"
              :required-mark="true"
              :clearable="true"
              :required="isSubmitted && dateError"
              :required-message="isSubmitted && dateError"
              format="DD.MM.YYYY, dddd"
              :editable="true"
              icon="calendar"
              placeholder="00.00.0000"
              :icon-right="true"
            />
          </div>
          <div class="form-group write-off-item-form__actor">
            <c-input
              v-model="form.actor"
              type="select"
              floating-label="Кем списан"
              :searchable="true"
              :clearable="true"
              :items="actors"
            />
          </div>
        </div>

        <div class="form-group inventory-item-form__comment">
          <c-textarea
            v-model="form.comment"
            type="textarea"
            :clearable="true"
            floating-label="Комментарий"
            max-length="500"
          />
        </div>

        <div class="form-group inventory-item-form__loader">
          <file-loader ref="loader" v-model="files" class="" format="all" :limit="10" />
        </div>
      </div>
    </div>

    <template #actions>
      <c-button class="write-off-item__cancel" label="Отменить" @click="close" />
      <c-button class="write-off-item__submit" color="primary" label="Списать" :pending="pending" @click="submit" />
    </template>
  </c-modal>
</template>

<script>
import moment from "moment";

import CModal from "@/components/scrollable-modal";
import Textarea from "@/components/textarea";
import Input from "@/components/controls2/Input";
import Button from "@/components/controls2/Button";
import FileLoader from "@/components/file-loader";
import { ACTION_WRITEOFF } from "../constants";
import { isDateValid } from "@/utils/date/valid";
import { DATE_FORMAT_ERROR, REQUIRED_ERROR } from "@/constants/errorFieldValues";
import { Breakpoint } from "@/utils/browser/breakpoint";

export default {
  components: {
    "c-textarea": Textarea,
    "c-input": Input,
    "c-button": Button,
    "file-loader": FileLoader,
    CModal,
  },

  data() {
    return {
      currentUser: null,
      active: false,
      isSubmitted: false,
      pending: false,
      isDesktop: false,
      breakpoint: null,

      item: null,

      form: {
        actor: null,
        date: "",
        comment: "",
      },

      files: [],
    };
  },

  computed: {
    actors() {
      return this.$store.getters["actors/activeActorsList"];
    },
    dateError() {
      if (!this.form.date) return REQUIRED_ERROR;
      return isDateValid(this.form.date) ? null : DATE_FORMAT_ERROR;
    },

    isValid() {
      return !this.dateError;
    },
    disableScroll() {
      if (!this.isDesktop) return false;
      return !this.files.length;
    },
  },

  watch: {
    active(v) {
      if (!v) this.reset();
    },
  },

  created() {
    this.currentUser = this.$store.state.userId;
    this.breakpoint = Breakpoint("(min-width: 700px)");
    const cb = (isDesktop) => {
      this.isDesktop = isDesktop;
    };
    this.breakpoint.subscribe(cb);
  },
  beforeUnmount() {
    this.breakpoint.dispose();
    this.breakpoint = null;
  },

  methods: {
    open(item) {
      this.item = item;

      this.form.date = moment().format("DD.MM.YYYY");
      const actor = this.actors.find((a) => {
        if (!a.userId) return false;
        return a.userId.toString() === this.currentUser;
      });
      this.form.actor = actor;
      this.active = true;
    },
    close() {
      this.active = false;
      this.$emit("close");
      this.reset();
    },

    reset() {
      this.isSubmitted = false;
      this.pending = false;

      this.form.actor = null;
      this.form.comment = "";
      this.form.date = "";
      this.files = [];
      if (this.$refs.loader) this.$refs.loader.reset();
    },

    getParams() {
      const { actor, date, comment } = this.form;

      return {
        action: ACTION_WRITEOFF,
        inventory_id: this.item.id,
        author_user_id: actor && actor.userId,
        author_worker_id: actor && actor.workerId,
        date,
        comment,

        files: this.files,
      };
    },
    submit() {
      this.isSubmitted = true;
      if (!this.isValid) return false;
      this.pending = true;

      const params = this.getParams();
      this.$store
        .dispatch("uniformItems/action", params)
        .then((res) => {
          if (!res.success) {
            console.error(res.message);
          } else {
            const nameText = this.item.name ? `(${this.item.name})` : "";

            this.$notify({
              group: "success-message",
              data: {
                textMessage: `Позиция <b>№${this.item.serialNumber} ${nameText}</b> успешно списана `,
              },
            });

            this.$emit("writtenOff", res.model);
            this.close();
          }
        })
        .catch((err) => {
          console.error(err);
          this.$notify({
            group: "error-message",
            data: {
              textMessage: "Произошла ошибка. Попробуйте ещё раз.",
            },
          });
        })
        .finally(() => {
          this.pending = false;
        });
      return true;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@/assets/styles/vars";

.write-off-item {
  padding: 0 2px;

  .item-name {
    font-weight: 400;
    font-size: 13px;
    margin-top: 5px;
  }

  &-form {
    .form-group {
      margin-bottom: 24px;

      &--invalid {
        margin-bottom: 35px;
      }
    }

    &__date {
      max-width: 125px;
      margin-right: 30px;
      padding-top: 6px;
    }

    &__actor {
      flex-grow: 1;
    }
  }

  @include onlyMobile {
    display: flex;
    flex-direction: column;
    max-height: 100%;
    overflow: hidden;

    &-form {
      padding-bottom: 60px;
      &__date {
        max-width: 125px;
      }
      &__actor {
        width: 100%;
      }
    }
  }
}
</style>
<style lang="scss">
.m-write-off-inventory-item {
  max-width: 590px !important;
}
</style>
